// Reusable 4-cell PIN input — auto-advance, backspace, paste, numeric keyboard

import { useRef, useCallback, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { PIN_LENGTH } from './pin-validation';

interface PinInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
}

export function PinInput({
  value,
  onChange,
  length = PIN_LENGTH,
  disabled = false,
  autoFocus = false,
  'aria-label': ariaLabel = 'PIN',
}: PinInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const digits = value.padEnd(length, '').slice(0, length).split('');

  const focusInput = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, length - 1));
      inputRefs.current[clamped]?.focus();
    },
    [length]
  );

  const handleChange = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const char = e.target.value;
      if (!/^\d$/.test(char)) {
        e.target.value = '';
        return;
      }
      const newDigits = [...digits];
      newDigits[index] = char;
      onChange(newDigits.join(''));
      if (index < length - 1) {
        focusInput(index + 1);
      }
    },
    [digits, onChange, length, focusInput]
  );

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        const newDigits = [...digits];
        if (digits[index]) {
          newDigits[index] = '';
          onChange(newDigits.join(''));
        } else if (index > 0) {
          newDigits[index - 1] = '';
          onChange(newDigits.join(''));
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        focusInput(index + 1);
      }
    },
    [digits, onChange, focusInput, length]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      if (pasted.length > 0) {
        onChange(pasted);
        focusInput(Math.min(pasted.length, length - 1));
      }
    },
    [onChange, length, focusInput]
  );

  return (
    <div className="flex gap-3 justify-center" role="group" aria-label={ariaLabel}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="\d"
          maxLength={1}
          value={digits[i] ? '\u2022' : ''}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`${ariaLabel} digit ${String(i + 1)}`}
          className="w-12 h-14 text-center text-2xl font-mono bg-input border border-border text-foreground rounded-lg focus:ring-2 focus:ring-ring focus:border-ring disabled:opacity-50 caret-transparent"
          autoComplete="off"
          data-testid={`pin-digit-${i}`}
        />
      ))}
    </div>
  );
}
